import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { useSlotSearch } from './SlotSearchContext';

const SLOT_TYPES = ['common', 'preferred', 'premium'];

interface MobileSlotFiltersProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileSlotFilters({ isOpen, onClose }: MobileSlotFiltersProps) {
  const { filters, updateFilters, clearFilters } = useSlotSearch();
  const [expandedSections, setExpandedSections] = useState<string[]>(['type', 'power', 'price', 'availability']);

  const toggleSection = (section: string) => {
    setExpandedSections(prev =>
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const handleSlotTypeChange = (type: string) => {
    const currentTypes = filters.slot_type || [];
    if (currentTypes.includes(type)) {
      updateFilters({ slot_type: currentTypes.filter(t => t !== type) });
    } else {
      updateFilters({ slot_type: [...currentTypes, type] });
    }
  };

  const activeFilterCount = [
    filters.slot_type && filters.slot_type.length > 0,
    filters.has_power !== undefined,
    filters.min_price !== undefined || filters.max_price !== undefined,
    filters.is_available !== undefined && filters.is_available !== true,
    filters.search && filters.search.length > 0,
  ].filter(Boolean).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-600" />
            <h2 className="font-semibold text-gray-900">Filters</h2>
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-primary rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        {/* Filter content */}
        <div className="p-4 space-y-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search slot number..."
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Slot Type Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('type')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Slot Type</span>
              {expandedSections.includes('type') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {expandedSections.includes('type') && (
              <div className="px-4 pb-3 space-y-2">
                {SLOT_TYPES.map((type) => (
                  <label key={type} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(filters.slot_type || []).includes(type)}
                      onChange={() => handleSlotTypeChange(type)}
                      className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 capitalize">{type}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Power Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('power')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Power Availability</span>
              {expandedSections.includes('power') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {expandedSections.includes('power') && (
              <div className="px-4 pb-3 space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="power-mobile"
                    checked={filters.has_power === undefined}
                    onChange={() => updateFilters({ has_power: undefined })}
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Any</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="power-mobile"
                    checked={filters.has_power === true}
                    onChange={() => updateFilters({ has_power: true })}
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Power Required</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="power-mobile"
                    checked={filters.has_power === false}
                    onChange={() => updateFilters({ has_power: false })}
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">No Power Needed</span>
                </label>
              </div>
            )}
          </div>

          {/* Price Range Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('price')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Price Range</span>
              {expandedSections.includes('price') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {expandedSections.includes('price') && (
              <div className="px-4 pb-3 flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  min="0"
                  value={filters.min_price ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateFilters({ min_price: undefined });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        updateFilters({ min_price: numValue });
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="text-gray-500 self-center">-</span>
                <input
                  type="number"
                  placeholder="Max"
                  min="0"
                  value={filters.max_price ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      updateFilters({ max_price: undefined });
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0) {
                        updateFilters({ max_price: numValue });
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}
          </div>

          {/* Availability Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleSection('availability')}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50"
            >
              <span className="font-medium text-gray-900">Availability</span>
              {expandedSections.includes('availability') ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </button>
            {expandedSections.includes('availability') && (
              <div className="px-4 pb-3 space-y-2">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability-mobile"
                    checked={filters.is_available === true}
                    onChange={() => updateFilters({ is_available: true })}
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Available Only</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="availability-mobile"
                    checked={filters.is_available === undefined}
                    onChange={() => updateFilters({ is_available: undefined })}
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show All</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex gap-3">
          <button
            onClick={clearFilters}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}
